import { useQuery } from "@tanstack/react-query";

import { httpClient } from "../../http-client";

export interface VocabExample {
  id: string;
  example: string;
  context: string | null;
  position: number;
}

export interface VocabCategoryRef {
  id: string;
  slug: string;
  name: string;
}

export interface VocabTermDetail {
  id: string;
  slug: string;
  term: string;
  part_of_speech: string | null;
  definition: string;
  definition_long: string | null;
  pronunciation: string | null;
  category_id: string | null;
  related_term_ids: string[];
  image_url: string | null;
  image_alt: string | null;
  published_at: string | null;
  created_at: string;
  updated_at: string;
  category: VocabCategoryRef | null;
  examples: VocabExample[];
}

export const vocabTermBySlugKey = (slug: string) =>
  ["vocabulary_terms", "by-slug", slug] as const;

export function useVocabTermBySlug(
  slug: string | null | undefined,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: vocabTermBySlugKey(slug ?? ""),
    queryFn: async (): Promise<VocabTermDetail | null> => {
      if (!slug) return null;
      try {
        const res = await httpClient.get<{ data: VocabTermDetail }>(
          `/vocabulary-terms/by-slug/${encodeURIComponent(slug)}`,
        );
        return res.data ?? null;
      } catch (err: unknown) {
        // 404 = unknown slug; surface as null so consumers can render
        // a graceful "not yet defined" state instead of an error toast.
        if (
          typeof err === "object" &&
          err !== null &&
          "status" in err &&
          (err as { status: number }).status === 404
        ) {
          return null;
        }
        throw err;
      }
    },
    enabled: Boolean(slug) && (options?.enabled ?? true),
    // Vocab definitions are essentially static during a session; cache
    // aggressively so a page full of hover links never re-fetches.
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });
}
